import { cn, supportsHover as supportsHoverHelper } from '@bridebook/aura';
import { cva } from 'class-variance-authority';
import { ComponentProps } from 'react';
import { TooltipInfoIcon } from '../tooltip-info-icon';
import { ResponsiveTooltip } from './responsive-tooltip';

/* ############################################################################
 *  TOUCH AREA WRAPPER
 *  This is for a wrapper to give a larger touch area to the icon. Important especially for mobile devices.
 * ######################################################################### */
const touchAreaWrapperVariants = cva('inline-flex items-center justify-center', {
  variants: {
    touchAreaSize: {
      sm: 'size-[16px] -translate-x-1/2',
      standard: 'top-[-8px] size-[32px] -translate-x-1/2',
      lg: 'top-[-14px] size-[44px] -translate-x-1/2',
    },
  },
  defaultVariants: {
    touchAreaSize: 'standard',
  },
});

type InfoIconWithTooltipProps = Omit<ComponentProps<typeof ResponsiveTooltip>, 'children'> & {
  iconProps?: ComponentProps<typeof TooltipInfoIcon>;
  touchAreaSize?: 'sm' | 'standard' | 'lg';
  classNames?: {
    touchArea?: string;
  };
  /**
   * Overrides hover detection. Rarely needed - primarily for Storybook demonstrations.
   * By default, hover support is automatically detected and the appropriate tooltip variant is rendered.
   * Additionally, on touch devices extra larger touch area is added for accessibility.
   */
  supportsHover?: boolean;
};

export const InfoIconWithResponsiveTooltip = ({
  className,
  iconProps,
  touchAreaSize = 'standard',
  variant = 'dark',
  classNames,
  supportsHover: supportsHoverProp,
  ...props
}: InfoIconWithTooltipProps) => {
  const supportsHover = supportsHoverProp ?? supportsHoverHelper();

  return (
    <ResponsiveTooltip
      supportsHover={supportsHoverProp}
      {...props}
      variant={variant}
      classNames={{ trigger: 'relative' }}>
      {!supportsHover && (
        <div
          className={cn(
            'absolute rounded-full',
            touchAreaWrapperVariants({ touchAreaSize }),
            classNames?.touchArea,
          )}
        />
      )}
      <TooltipInfoIcon className={cn(className, 'relative')} variant={variant} {...iconProps} />
    </ResponsiveTooltip>
  );
};
